import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import axios from "axios";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectFade, Autoplay, Pagination, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/pagination";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  ArrowDown,
  CheckCircle2,
  AlertCircle,
  Check,
  CircleSlash,
  Search,
  Target,
  ShieldCheck,
  Handshake,
  Layers,
  Building2,
  MapPin,
  Sparkles,
  FileCheck2,
  Zap,
} from "lucide-react";

import { BUILDER_HERO_IMAGES, ABOUT_BG, GALLERY_IMAGES, SITE } from "../lib/config";
import { IbexMark } from "../components/site/IbexMark";
import BeforeAfter from "../components/site/BeforeAfter";
import "swiper/css/navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const scrollToId = (id) => (e) => {
  e && e.preventDefault && e.preventDefault();
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

/* ---------------- SECTION 1 — HERO ---------------- */
const BuildersHero = () => {
  const swiperRef = useRef(null);
  const reduced =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return (
    <section
      id="top"
      className="relative w-full overflow-hidden"
      style={{ height: "100svh", minHeight: "640px" }}
      data-testid="builders-hero-section"
    >
      <Swiper
        onSwiper={(s) => (swiperRef.current = s)}
        modules={[EffectFade, Autoplay, Pagination]}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        loop
        speed={1600}
        autoplay={
          reduced
            ? false
            : { delay: 6000, disableOnInteraction: false, pauseOnMouseEnter: false }
        }
        pagination={{ clickable: true }}
        className="absolute inset-0"
        style={{ position: "absolute", inset: 0, height: "100%", width: "100%" }}
        data-testid="builders-hero-swiper"
      >
        {BUILDER_HERO_IMAGES.map((img, i) => (
          <SwiperSlide key={i} className="!h-full !w-full">
            <div className="relative h-full w-full overflow-hidden">
              <img
                src={img.src}
                alt={img.alt}
                loading={i === 0 ? "eager" : "lazy"}
                className={`absolute inset-0 h-full w-full object-cover ${
                  reduced ? "" : "kenburns"
                }`}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Scrims */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(115deg, rgba(18,43,32,0.82) 0%, rgba(18,43,32,0.45) 55%, rgba(18,43,32,0.12) 100%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(90% 80% at 8% 90%, rgba(18,43,32,0.7) 0%, rgba(18,43,32,0) 65%)",
        }}
      />

      <div className="relative z-10 h-full flex items-end lg:items-center">
        <div className="max-w-7xl mx-auto w-full px-6 md:px-10 pt-32 pb-24 lg:py-32">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="eyebrow"
            style={{ color: "#C6A15B" }}
            data-testid="builders-hero-eyebrow"
          >
            Land Acquisition for Builders &amp; Developers
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5, ease: "easeOut" }}
            className="font-heading font-medium text-[#FAF7F2] mt-5 max-w-3xl"
            style={{
              fontSize: "clamp(2.6rem, 5.4vw, 4.6rem)",
              lineHeight: 1.05,
              letterSpacing: "-0.015em",
            }}
            data-testid="builders-hero-headline"
          >
            We Find the Dirt.{" "}
            <span className="gold-italic">You Build the Vision.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: "easeOut" }}
            className="mt-7 max-w-2xl text-[#FAF7F2]/90 font-body text-base md:text-lg"
            style={{ lineHeight: 1.7 }}
          >
            Ibex is an off-market land pipeline for builders — but not all of
            them. We partner with a limited number of builders in each market,
            learn their buy box inside and out, and put our full sourcing
            effort behind them. Fewer partners. Deeper relationships. Better
            dirt.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0, ease: "easeOut" }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={scrollToId("buy-box")}
              className="btn-primary"
              data-testid="builders-hero-cta-primary"
            >
              Submit Your Buy Box
            </button>
            <button
              onClick={scrollToId("how-we-work")}
              className="btn-ghost on-dark"
              data-testid="builders-hero-cta-secondary"
            >
              How We Work
            </button>
          </motion.div>
        </div>
      </div>

      <motion.a
        href="#problem"
        onClick={scrollToId("problem")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#C6A15B]"
        aria-label="Scroll to next section"
      >
        <span
          className="text-[0.6rem]"
          style={{ letterSpacing: "0.32em", textTransform: "uppercase" }}
        >
          Scroll
        </span>
        <div className="w-px h-10 bg-[#C6A15B]/60" />
        <ChevronDown size={18} className="scroll-indicator" strokeWidth={1.5} />
      </motion.a>
    </section>
  );
};

/* ---------------- SECTION 2 — PROBLEM WE SOLVE ---------------- */
const Problem = () => (
  <section
    id="problem"
    className="section-pad"
    style={{ background: "#FAF7F2" }}
    data-testid="builders-problem-section"
  >
    <div className="max-w-4xl mx-auto px-6 md:px-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <p className="eyebrow">Why Ibex Exists</p>
        <h2
          className="font-heading font-medium mt-4"
          style={{
            fontSize: "clamp(2rem, 4vw, 3.4rem)",
            lineHeight: 1.1,
            color: "#122B20",
            letterSpacing: "-0.015em",
          }}
        >
          The Best Lots <span className="gold-italic">Never Hit the Market.</span>
        </h2>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
        className="mt-10 space-y-6 max-w-3xl"
      >
        <p
          className="font-body text-[#2B2926]/90"
          style={{ fontSize: "1.08rem", lineHeight: 1.85 }}
        >
          You know it and we know it: by the time a good lot shows up on the
          MLS, the margin is gone. The deals worth building on are sitting with
          long-term owners, tired landlords, out-of-state heirs, and sellers
          who will never call an agent. Finding them takes hundreds of hours of
          county records, parcel maps, and phone calls — hours your team should
          be spending building.
        </p>
        <p
          className="font-body text-[#2B2926]/90"
          style={{ fontSize: "1.08rem", lineHeight: 1.85 }}
        >
          That’s the work we do. Ibex hunts on-market and off, underwrites
          against your numbers, and brings you opportunities that already fit
          the way you build — so your acquisition pipeline stays full without
          growing your headcount.
        </p>
      </motion.div>
    </div>
  </section>
);

/* ---------------- SECTION 3 — HOW WE WORK ---------------- */
const steps = [
  {
    n: "01",
    icon: Target,
    title: "Give Us Your Buy Box",
    body: "Target markets, lot sizes, zoning, utility requirements, price ceilings, product type, close timeline — the tighter the criteria, the better we hunt. Five minutes with the form below is all it takes.",
  },
  {
    n: "02",
    icon: Search,
    title: "We Hunt",
    body: "Listed, expired, stale, and fully off-market. County records, parcel data, absentee owners, long-term holds, assemblage plays, teardown candidates on transitional streets. We work the sources most acquisition teams never get to.",
  },
  {
    n: "03",
    icon: ShieldCheck,
    title: "We Screen Before You See It",
    body: "Zoning, legal access, utilities, floodplain, slope, comps, and resale logic — checked against your criteria before a deal ever reaches your inbox. And we’re honest about what’s verified versus what still needs confirmation. If we haven’t proven a lot is buildable, we won’t tell you it is. You get what we know, what we don’t, and what’s left to check — clearly labeled.",
  },
  {
    n: "04",
    icon: Handshake,
    title: "You Review. We Close.",
    body: "Like the deal? We deliver it contract-ready and coordinate with the title company for a clean, fast closing. Pass? Tell us why, and the next batch gets sharper. Every “no” tunes the pipeline.",
  },
];

const HowWeWork = () => (
  <section
    id="how-we-work"
    className="section-pad"
    style={{ background: "#F0EAE0" }}
    data-testid="builders-how-section"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="max-w-2xl"
      >
        <p className="eyebrow">A Simple Operating Rhythm</p>
        <h2
          className="font-heading font-medium mt-4"
          style={{
            fontSize: "clamp(2rem, 4vw, 3.4rem)",
            lineHeight: 1.1,
            color: "#122B20",
            letterSpacing: "-0.015em",
          }}
        >
          Your Buy Box Is <span className="gold-italic">Our Blueprint.</span>
        </h2>
      </motion.div>

      <div className="relative mt-16 md:mt-20">
        {/* Desktop connecting line spanning all 4 columns */}
        <div
          className="hidden lg:block absolute top-[4.25rem] left-[12.5%] right-[12.5%] h-px"
          style={{
            background:
              "linear-gradient(90deg, rgba(198,161,91,0) 0%, rgba(198,161,91,0.55) 15%, rgba(198,161,91,0.55) 85%, rgba(198,161,91,0) 100%)",
          }}
        />

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1 } } }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {steps.map((s) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.n}
                variants={{
                  hidden: { opacity: 0, y: 24 },
                  show: { opacity: 1, y: 0 },
                }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="glass-light p-7 md:p-8 relative"
                data-testid={`builders-step-${s.n}`}
              >
                <div className="flex items-start justify-between">
                  <div
                    className="font-heading font-medium leading-none"
                    style={{
                      fontSize: "2.4rem",
                      color: "#C6A15B",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {s.n}
                  </div>
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{
                      background: "rgba(27,67,50,0.08)",
                      border: "1px solid rgba(27,67,50,0.15)",
                    }}
                  >
                    <Icon size={18} color="#1B4332" strokeWidth={1.5} />
                  </div>
                </div>
                <h3
                  className="font-heading font-medium mt-5 text-xl md:text-[1.35rem]"
                  style={{ color: "#122B20", lineHeight: 1.25 }}
                >
                  {s.title}
                </h3>
                <p
                  className="mt-4 font-body text-[#2B2926]/85"
                  style={{ fontSize: "0.94rem", lineHeight: 1.7 }}
                >
                  {s.body}
                </p>
              </motion.article>
            );
          })}
        </motion.div>
      </div>
    </div>
  </section>
);

/* ---------------- SECTION 4 — WHAT WE DELIVER ---------------- */
const deliverables = [
  {
    icon: Search,
    title: "First-Look Deal Flow",
    body: "Partners see every fitting deal first — sourced off-market, never blasted to a list.",
  },
  {
    icon: Building2,
    title: "Infill & Teardown Specialists",
    body: "Scrape-and-build candidates and skinny city lots where the land is the play.",
  },
  {
    icon: Layers,
    title: "Assemblage Opportunities",
    body: "Adjacent parcels combined into sites worth more together than apart.",
  },
  {
    icon: Sparkles,
    title: "Luxury to Transitional Markets",
    body: "From established high-end enclaves to revitalizing streets on the way up — wherever your product fits.",
  },
  {
    icon: FileCheck2,
    title: "Honest Due Diligence",
    body: "Verified facts, flagged unknowns, zero wishful thinking. Your underwriting starts from truth.",
  },
  {
    icon: Zap,
    title: "Clean, Fast Execution",
    body: "Clear contracts, responsive communication, title-company closings, no drama.",
  },
];

const WhatWeDeliver = () => (
  <section
    id="what-we-deliver"
    className="section-pad"
    style={{ background: "#FAF7F2" }}
    data-testid="builders-deliver-section"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="max-w-2xl"
      >
        <p className="eyebrow">The Pipeline</p>
        <h2
          className="font-heading font-medium mt-4"
          style={{
            fontSize: "clamp(2rem, 4vw, 3.4rem)",
            lineHeight: 1.1,
            color: "#122B20",
            letterSpacing: "-0.015em",
          }}
        >
          Built For How Builders{" "}
          <span className="gold-italic">Actually Buy.</span>
        </h2>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
        className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
      >
        {deliverables.map((it, i) => {
          const Icon = it.icon;
          return (
            <motion.article
              key={i}
              variants={{
                hidden: { opacity: 0, y: 20 },
                show: { opacity: 1, y: 0 },
              }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="group relative p-7 md:p-8 rounded-2xl transition-all duration-300"
              style={{
                background: "#FAF7F2",
                border: "1px solid rgba(168,162,154,0.35)",
              }}
              data-testid={`builders-deliver-${i}`}
            >
              <div className="flex items-start justify-between">
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center"
                  style={{
                    background: "rgba(27,67,50,0.06)",
                    border: "1px solid rgba(27,67,50,0.15)",
                  }}
                >
                  <Icon size={20} color="#1B4332" strokeWidth={1.5} />
                </div>
                <div
                  className="font-heading text-sm opacity-40 group-hover:opacity-100 transition-opacity"
                  style={{ color: "#C6A15B" }}
                >
                  0{i + 1}
                </div>
              </div>
              <h3
                className="font-heading font-medium mt-6 text-xl md:text-[1.35rem]"
                style={{ color: "#122B20", lineHeight: 1.25 }}
              >
                {it.title}
              </h3>
              <p
                className="mt-3 font-body text-[#2B2926]/80"
                style={{ fontSize: "0.94rem", lineHeight: 1.65 }}
              >
                {it.body}
              </p>
              <style>{`
                [data-testid="builders-deliver-${i}"]:hover {
                  transform: translateY(-4px);
                  box-shadow: 0 24px 40px -24px rgba(18,43,32,0.25);
                  border-color: rgba(198,161,91,0.4);
                }
              `}</style>
            </motion.article>
          );
        })}
      </motion.div>
    </div>
  </section>
);

/* ---------------- PARTNERSHIP STANDARD ---------------- */
const fitYes = [
  "You’re buying multiple lots per quarter — or building toward it",
  "You want a long-term sourcing partner, not a one-off deal",
  "You give straight feedback on passes so our hunting gets sharper",
  "You move decisively when a deal fits your box",
  "You close what you commit to",
];
const fitNo = [
  {
    line: "You need one lot, one time",
    sub: "No hard feelings — check back when you’re scaling.",
  },
  { line: "You’d rather watch a blast list than build a relationship" },
  { line: "Deals sit in your inbox for weeks without an answer" },
];

const PartnershipStandard = () => (
  <section
    id="partnership"
    className="section-pad"
    style={{ background: "#F0EAE0" }}
    data-testid="builders-partnership-section"
  >
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="max-w-3xl"
      >
        <p className="eyebrow">How We Partner</p>
        <h2
          className="font-heading font-medium mt-4"
          style={{
            fontSize: "clamp(2rem, 4vw, 3.4rem)",
            lineHeight: 1.1,
            color: "#122B20",
            letterSpacing: "-0.015em",
          }}
        >
          We Don’t Work With Every Builder.{" "}
          <span className="gold-italic">That’s the Point.</span>
        </h2>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
        className="mt-10 space-y-6 max-w-3xl"
      >
        <p
          className="font-body text-[#2B2926]/90"
          style={{ fontSize: "1.05rem", lineHeight: 1.8 }}
        >
          Most land wholesalers blast every deal to a list of five hundred
          buyers and take the fastest yes. We built Ibex to be the opposite. We
          keep our partner roster deliberately small — a limited number of
          builders per market — because real sourcing takes real focus, and
          undivided attention doesn’t scale.
        </p>
        <p
          className="font-body text-[#2B2926]/90"
          style={{ fontSize: "1.05rem", lineHeight: 1.8 }}
        >
          When we hunt in your market, we’re hunting for you. Our partners get
          first look at every opportunity that fits their buy box before anyone
          else sees it. In return, we look for builders who want more than a
          one-time deal: teams buying multiple lots a quarter, who share honest
          feedback, move decisively, and want an acquisition partner in their
          corner year after year.
        </p>
        <p
          className="font-body text-[#2B2926]/90"
          style={{ fontSize: "1.05rem", lineHeight: 1.8 }}
        >
          That’s the relationship we’re building — mutually beneficial, built
          on trust, and aimed at the same outcome: growing bigger, better
          businesses together.
        </p>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        variants={{ hidden: {}, show: { transition: { staggerChildren: 0.12 } } }}
        className="mt-14 md:mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-stretch"
      >
        {/* A strong fit */}
        <motion.div
          variants={{
            hidden: { opacity: 0, y: 20 },
            show: { opacity: 1, y: 0 },
          }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="glass-light p-7 md:p-8 h-full flex flex-col"
          data-testid="partnership-fit-yes"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center"
              style={{
                background: "rgba(198,161,91,0.14)",
                border: "1px solid rgba(198,161,91,0.55)",
              }}
            >
              <Check size={18} color="#C6A15B" strokeWidth={2.25} />
            </div>
            <h3
              className="font-heading font-medium"
              style={{ color: "#122B20", fontSize: "1.3rem" }}
            >
              A strong fit
            </h3>
          </div>
          <ul className="mt-6 space-y-3.5">
            {fitYes.map((line, i) => (
              <li key={i} className="flex items-start gap-3">
                <div
                  className="shrink-0 mt-1 w-4 h-4 rounded-full flex items-center justify-center"
                  style={{
                    background: "rgba(198,161,91,0.18)",
                    border: "1px solid rgba(198,161,91,0.6)",
                  }}
                >
                  <Check size={10} color="#C6A15B" strokeWidth={2.5} />
                </div>
                <span
                  className="font-body text-[#2B2926]"
                  style={{ fontSize: "0.98rem", lineHeight: 1.6 }}
                >
                  {line}
                </span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Probably not a fit */}
        <motion.div
          variants={{
            hidden: { opacity: 0, y: 20 },
            show: { opacity: 1, y: 0 },
          }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="p-7 md:p-8 rounded-2xl h-full flex flex-col"
          style={{
            background: "rgba(250,247,242,0.55)",
            border: "1px solid rgba(168,162,154,0.45)",
          }}
          data-testid="partnership-fit-no"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center"
              style={{
                background: "rgba(168,162,154,0.15)",
                border: "1px solid rgba(168,162,154,0.55)",
              }}
            >
              <CircleSlash size={18} color="#A8A29A" strokeWidth={1.75} />
            </div>
            <h3
              className="font-heading font-medium"
              style={{ color: "#2B2926", fontSize: "1.3rem" }}
            >
              Probably not a fit
            </h3>
          </div>
          <ul className="mt-6 space-y-4">
            {fitNo.map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <div
                  className="shrink-0 mt-1 w-4 h-4 rounded-full"
                  style={{
                    border: "1px solid rgba(168,162,154,0.7)",
                    background: "transparent",
                  }}
                />
                <div>
                  <p
                    className="font-body text-[#2B2926]/90"
                    style={{ fontSize: "0.98rem", lineHeight: 1.55 }}
                  >
                    {item.line}
                  </p>
                  {item.sub && (
                    <p
                      className="mt-1"
                      style={{
                        color: "#A8A29A",
                        fontSize: "0.82rem",
                        lineHeight: 1.55,
                        fontStyle: "italic",
                      }}
                    >
                      {item.sub}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="mt-14 md:mt-16 text-center"
      >
        <p
          className="font-heading"
          style={{
            color: "#122B20",
            fontSize: "clamp(1.25rem, 2vw, 1.5rem)",
            lineHeight: 1.35,
          }}
        >
          If the left column sounds like how you build,{" "}
          <span className="gold-italic">let’s talk.</span>
        </p>
        <a
          href="#buy-box"
          onClick={scrollToId("buy-box")}
          className="inline-flex items-center gap-2 mt-5 group"
          data-testid="partnership-share-buybox-link"
          style={{
            color: "#C6A15B",
            fontSize: "0.9rem",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            fontWeight: 500,
          }}
        >
          <span className="border-b border-[#C6A15B]/50 group-hover:border-[#C6A15B] pb-0.5 transition-colors">
            Share your buy box
          </span>
          <ArrowDown
            size={16}
            strokeWidth={1.75}
            className="transition-transform duration-200 group-hover:translate-y-0.5"
          />
        </a>
      </motion.div>
    </div>
  </section>
);

/* ---------------- GALLERY (full-bleed sliding carousel) ---------------- */
const Gallery = () => {
  const reduced =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return (
    <section
      id="portfolio"
      className="relative"
      style={{ background: "#122B20", paddingTop: "clamp(4rem, 8vw, 7rem)", paddingBottom: "clamp(4rem, 8vw, 7rem)" }}
      data-testid="builders-gallery-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="max-w-2xl"
        >
          <p className="eyebrow">Recent Work</p>
          <h2
            className="font-heading font-medium mt-4"
            style={{
              fontSize: "clamp(2rem, 4vw, 3.4rem)",
              lineHeight: 1.1,
              color: "#FAF7F2",
              letterSpacing: "-0.015em",
            }}
          >
            Lots We Found.{" "}
            <span className="gold-italic">Homes That Followed.</span>
          </h2>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.9, delay: 0.15, ease: "easeOut" }}
        className="relative mt-14 md:mt-20"
      >
        <Swiper
          modules={[Autoplay, Pagination, Navigation]}
          slidesPerView={1}
          loop
          speed={900}
          spaceBetween={0}
          autoplay={
            reduced
              ? false
              : { delay: 5200, disableOnInteraction: false, pauseOnMouseEnter: true }
          }
          pagination={{ clickable: true, el: ".gallery-pagination" }}
          navigation={{
            prevEl: ".gallery-prev",
            nextEl: ".gallery-next",
          }}
          className="w-full"
          style={{ width: "100%" }}
          data-testid="gallery-swiper"
        >
          {GALLERY_IMAGES.map((img, i) => (
            <SwiperSlide key={i} data-testid={`gallery-slide-${i}`}>
              <div
                className="relative w-full overflow-hidden"
                style={{ aspectRatio: "16 / 9", maxHeight: "78vh" }}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading={i === 0 ? "eager" : "lazy"}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                {/* Subtle vignette edges for polish */}
                <div
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(90deg, rgba(18,43,32,0.35) 0%, rgba(18,43,32,0) 12%, rgba(18,43,32,0) 88%, rgba(18,43,32,0.35) 100%)",
                  }}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Custom controls */}
        <button
          type="button"
          aria-label="Previous slide"
          data-testid="gallery-prev"
          className="gallery-prev absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200"
          style={{
            background: "rgba(250,247,242,0.14)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            border: "1px solid rgba(198,161,91,0.55)",
            color: "#FAF7F2",
          }}
        >
          <ChevronLeft size={20} strokeWidth={1.6} />
        </button>
        <button
          type="button"
          aria-label="Next slide"
          data-testid="gallery-next"
          className="gallery-next absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200"
          style={{
            background: "rgba(250,247,242,0.14)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            border: "1px solid rgba(198,161,91,0.55)",
            color: "#FAF7F2",
          }}
        >
          <ChevronRight size={20} strokeWidth={1.6} />
        </button>

        <div className="gallery-pagination flex justify-center gap-2 mt-8" />
        <style>{`
          .gallery-prev:hover, .gallery-next:hover {
            background: rgba(198,161,91,0.30) !important;
            border-color: #C6A15B !important;
          }
          .gallery-pagination .swiper-pagination-bullet {
            background: rgba(250,247,242,0.35) !important;
          }
          .gallery-pagination .swiper-pagination-bullet-active {
            background: #C6A15B !important;
          }
        `}</style>
      </motion.div>
    </section>
  );
};

/* ---------------- SECTION 5 — I.B.E.X., BUILDER EDITION ---------------- */
const builderValues = [
  {
    letter: "I",
    label: "Invest",
    body: "We invest the sourcing hours, the data spend, and the legwork before you commit a dollar.",
  },
  {
    letter: "B",
    label: "Build",
    body: "Everything we do points at one outcome: you building. Your product is the whole point of our pipeline.",
  },
  {
    letter: "E",
    label: "Elevate",
    body: "The right build on the right lot lifts an entire street. We hunt the lots that make that math work.",
  },
  {
    letter: "X",
    label: "Expand",
    body: "Entering a new market? We scale our sourcing into it with you — most states, wherever opportunity exists.",
  },
];

const BuilderEdition = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);
  const midY = useTransform(scrollYProgress, [0, 1], ["4%", "-4%"]);

  return (
    <section
      id="builder-edition"
      ref={ref}
      className="relative overflow-hidden section-pad"
      style={{ background: "#122B20", color: "#FAF7F2" }}
      data-testid="builders-edition-section"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-0" aria-hidden="true">
        <img
          src={ABOUT_BG}
          alt=""
          className="absolute inset-0 w-full h-[120%] object-cover"
          loading="lazy"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(18,43,32,0.85) 0%, rgba(18,43,32,0.92) 100%)",
          }}
        />
      </motion.div>

      <motion.div
        style={{ y: midY }}
        className="pointer-events-none absolute left-[-4%] top-1/2 -translate-y-1/2 w-[560px] h-[560px] md:w-[720px] md:h-[720px]"
        aria-hidden="true"
      >
        <IbexMark className="w-full h-full" strokeWidth={1} />
        <style>{`
          [data-testid="builders-edition-section"] svg { color: rgba(198,161,91,0.09); }
        `}</style>
      </motion.div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <p className="eyebrow">How We Do Business</p>
          <h2
            className="font-heading font-medium mt-4 max-w-3xl"
            style={{
              fontSize: "clamp(2rem, 4.2vw, 3.6rem)",
              lineHeight: 1.1,
              color: "#FAF7F2",
              letterSpacing: "-0.015em",
            }}
          >
            Sure-Footed <span className="gold-italic">Where Others Slip.</span>
          </h2>
          <p
            className="mt-8 font-body max-w-2xl"
            style={{
              color: "rgba(250,247,242,0.88)",
              lineHeight: 1.8,
              fontSize: "1.05rem",
            }}
          >
            We named this company after the ibex — the mountain goat that
            climbs terrain nothing else can. Complicated parcels, tangled
            titles, streets in transition: that’s our footing. And the name is
            also our operating system:
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1 } } }}
          className="mt-14 md:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {builderValues.map((v) => (
            <motion.div
              key={v.letter}
              variants={{
                hidden: { opacity: 0, y: 20 },
                show: { opacity: 1, y: 0 },
              }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              data-testid={`builders-value-${v.letter}`}
            >
              <div className="flex items-baseline gap-4">
                <div
                  className="font-heading font-medium leading-none"
                  style={{
                    color: "#C6A15B",
                    fontSize: "3rem",
                    letterSpacing: "-0.02em",
                  }}
                >
                  {v.letter}
                </div>
                <div
                  className="font-heading font-medium"
                  style={{ color: "#FAF7F2", fontSize: "1.5rem" }}
                >
                  {v.label}
                </div>
              </div>
              <div
                className="mt-4 h-px"
                style={{ background: "rgba(198,161,91,0.3)" }}
              />
              <p
                className="mt-4 font-body"
                style={{
                  color: "rgba(250,247,242,0.82)",
                  lineHeight: 1.7,
                  fontSize: "0.95rem",
                }}
              >
                {v.body}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

/* ---------------- SECTION 6 — WHO WE ARE ---------------- */
const WhoWeAre = () => (
  <section
    id="who-we-are"
    className="section-pad"
    style={{ background: "#F0EAE0" }}
    data-testid="builders-who-section"
  >
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="lg:col-span-5"
        >
          <div
            className="w-full max-w-sm aspect-[4/5] rounded-2xl mx-auto relative overflow-hidden"
            style={{
              background:
                "linear-gradient(160deg, rgba(198,161,91,0.20) 0%, rgba(27,67,50,0.55) 100%)",
              border: "1px solid rgba(198,161,91,0.5)",
              boxShadow: "0 40px 60px -40px rgba(18,43,32,0.35)",
            }}
            data-testid="builders-founders-card"
          >
            <img
              src={SITE.founderPhoto}
              alt="Brandon and Katana — founders of Ibex Acquisitions & Development"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              data-testid="builders-founders-photo"
            />
            <div
              className="absolute bottom-0 inset-x-0 h-24"
              style={{
                background:
                  "linear-gradient(180deg, rgba(18,43,32,0) 0%, rgba(18,43,32,0.7) 100%)",
              }}
            />
            <div className="absolute bottom-5 inset-x-0 text-center">
              <p
                className="font-heading"
                style={{ color: "#FAF7F2", fontSize: "1.05rem" }}
              >
                Brandon &amp; Katana
              </p>
              <p
                className="mt-1"
                style={{
                  color: "#C6A15B",
                  fontSize: "0.68rem",
                  letterSpacing: "0.24em",
                  textTransform: "uppercase",
                }}
              >
                Founders
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }}
          className="lg:col-span-7"
        >
          <p className="eyebrow">The People Behind the Pipeline</p>
          <h2
            className="font-heading font-medium mt-4"
            style={{
              fontSize: "clamp(2rem, 4vw, 3.4rem)",
              lineHeight: 1.1,
              color: "#122B20",
              letterSpacing: "-0.015em",
            }}
          >
            A Family Company{" "}
            <span className="gold-italic">That Does What It Says.</span>
          </h2>
          <p
            className="mt-6 font-body text-[#2B2926]/90 max-w-2xl"
            style={{ fontSize: "1.05rem", lineHeight: 1.8 }}
          >
            Ibex Acquisitions &amp; Development is run by Brandon and Katana —
            a husband-and-wife team who built this company on straight talk,
            fair numbers, and follow-through. When you work with Ibex, you’re
            not routed through an acquisitions bullpen. You talk to the people
            whose name is on the door, whose reputation rides on every deal,
            and who answer the phone when something needs solving.
          </p>
          <Link
            to="/#about"
            className="inline-flex items-center gap-2 mt-8 group"
            data-testid="builders-more-about-link"
            style={{
              color: "#C6A15B",
              fontSize: "0.9rem",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              fontWeight: 500,
            }}
          >
            <span className="border-b border-[#C6A15B]/50 group-hover:border-[#C6A15B] pb-0.5 transition-colors">
              More about us
            </span>
            <ArrowRight size={16} strokeWidth={1.75} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ---------------- SECTION 7 — BUY BOX FORM ---------------- */
const LOT_TYPES = [
  "Infill lot",
  "Teardown",
  "Acreage",
  "Subdivision parcel",
  "Multifamily or townhome parcel",
  "Assemblage",
];

const BuyBoxForm = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    defaultValues: {
      full_name: "",
      company: "",
      email: "",
      phone: "",
      buyer_type: "",
      target_markets: "",
      lot_types: [],
      lot_budget: "",
      utility_requirements: "",
      product_price: "",
      close_timeline: "",
      lot_volume: "",
      notes: "",
      consent: false,
      website: "",
    },
  });

  const [status, setStatus] = useState({ state: "idle", error: "" });

  const onSubmit = async (data) => {
    setStatus({ state: "submitting", error: "" });
    try {
      const res = await axios.post(`${API}/builder-leads`, data, { timeout: 15000 });
      if (res.data && res.data.status === "success") {
        setStatus({ state: "success", error: "" });
        reset();
      } else {
        throw new Error("Unexpected response");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong sending your buy box. Please try again.";
      setStatus({ state: "error", error: msg });
    }
  };

  return (
    <section
      id="buy-box"
      className="section-pad relative"
      style={{ background: "#FAF7F2" }}
      data-testid="buy-box-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left column */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="lg:col-span-5"
          >
            <p className="eyebrow">Start the Conversation</p>
            <h2
              className="font-heading font-medium mt-4"
              style={{
                fontSize: "clamp(2rem, 4vw, 3.4rem)",
                lineHeight: 1.1,
                color: "#122B20",
                letterSpacing: "-0.015em",
              }}
            >
              Share Your Buy Box.{" "}
              <span className="gold-italic">Let’s See If We Fit.</span>
            </h2>
            <p
              className="mt-6 font-body text-[#2B2926]/90 max-w-lg"
              style={{ fontSize: "1.05rem", lineHeight: 1.75 }}
            >
              Your buy box is where every great partnership starts. Send us
              yours and we’ll set up a call to talk markets, product, and
              volume — and whether Ibex is the right acquisition partner for
              how you build. No retainers, no exclusivity contracts, no
              obligation on either side of the table.
            </p>
            <ul className="mt-8 space-y-3 max-w-md">
              {[
                "Your criteria stay confidential",
                "Limited partners per market",
                "Deals only — we don’t spam pipelines",
              ].map((line) => (
                <li key={line} className="flex items-center gap-3">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center"
                    style={{ background: "rgba(198,161,91,0.15)" }}
                  >
                    <CheckCircle2 size={14} color="#C6A15B" strokeWidth={2} />
                  </div>
                  <span
                    className="font-body text-[#2B2926]/90"
                    style={{ fontSize: "0.95rem" }}
                  >
                    {line}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Right column: form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }}
            className="lg:col-span-7"
          >
            <div className="glass-light p-6 md:p-10 relative" data-testid="buy-box-form-card">
              {status.state === "success" ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="py-8 text-center"
                  data-testid="buy-box-success"
                >
                  <motion.div
                    initial={{ scale: 0.4, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    className="mx-auto w-20 h-20 rounded-full flex items-center justify-center"
                    style={{
                      background: "rgba(27,67,50,0.08)",
                      border: "2px solid #1B4332",
                    }}
                  >
                    <CheckCircle2 size={44} color="#1B4332" strokeWidth={1.8} />
                  </motion.div>
                  <h3
                    className="font-heading font-medium mt-6"
                    style={{ color: "#122B20", fontSize: "1.75rem" }}
                  >
                    Buy box <span className="gold-italic">received.</span>
                  </h3>
                  <p
                    className="mt-4 font-body text-[#2B2926]/80 max-w-md mx-auto"
                    style={{ fontSize: "1rem", lineHeight: 1.7 }}
                  >
                    We’ll review it against our current partner roster in your
                    market and reach out within two business days to set up a
                    conversation.
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus({ state: "idle", error: "" })}
                    className="btn-ghost mt-8"
                    data-testid="buy-box-reset"
                  >
                    Submit another buy box
                  </button>
                </motion.div>
              ) : (
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  noValidate
                  data-testid="buy-box-form"
                  className="space-y-5"
                >
                  {/* Honeypot */}
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: "-9999px",
                      width: 1,
                      height: 1,
                      overflow: "hidden",
                    }}
                  >
                    <label>
                      Website
                      <input type="text" tabIndex={-1} autoComplete="off" {...register("website")} />
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="field">
                      <input
                        id="bb-full_name"
                        type="text"
                        placeholder=" "
                        data-testid="bb-input-full-name"
                        {...register("full_name", { required: "Please tell us your name." })}
                      />
                      <label htmlFor="bb-full_name">Full name*</label>
                      {errors.full_name && (
                        <p className="error-text" data-testid="bb-error-full-name">
                          {errors.full_name.message}
                        </p>
                      )}
                    </div>
                    <div className="field">
                      <input
                        id="bb-company"
                        type="text"
                        placeholder=" "
                        data-testid="bb-input-company"
                        {...register("company", { required: "Company is required." })}
                      />
                      <label htmlFor="bb-company">Company*</label>
                      {errors.company && (
                        <p className="error-text" data-testid="bb-error-company">
                          {errors.company.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="field">
                      <input
                        id="bb-email"
                        type="email"
                        placeholder=" "
                        data-testid="bb-input-email"
                        {...register("email", {
                          required: "Email is required.",
                          pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: "Please enter a valid email.",
                          },
                        })}
                      />
                      <label htmlFor="bb-email">Email*</label>
                      {errors.email && (
                        <p className="error-text" data-testid="bb-error-email">
                          {errors.email.message}
                        </p>
                      )}
                    </div>
                    <div className="field">
                      <input
                        id="bb-phone"
                        type="tel"
                        placeholder=" "
                        data-testid="bb-input-phone"
                        {...register("phone", {
                          required: "Phone number is required.",
                          validate: (v) => {
                            const d = (v || "").replace(/\D/g, "");
                            return d.length >= 7 || "That doesn’t look like a phone number.";
                          },
                        })}
                      />
                      <label htmlFor="bb-phone">Phone*</label>
                      {errors.phone && (
                        <p className="error-text" data-testid="bb-error-phone">
                          {errors.phone.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <SelectField
                    id="bb-buyer_type"
                    label="Buyer type*"
                    testId="bb-input-buyer-type"
                    register={register("buyer_type", { required: "Please pick a buyer type." })}
                    options={[
                      "Production builder",
                      "Custom builder",
                      "Luxury builder",
                      "Scattered-lot builder",
                      "Developer",
                      "Investor",
                    ]}
                    error={errors.buyer_type}
                    errorTestId="bb-error-buyer-type"
                  />

                  <div className="field">
                    <input
                      id="bb-target_markets"
                      type="text"
                      placeholder=" "
                      data-testid="bb-input-target-markets"
                      {...register("target_markets", {
                        required: "Give us at least one market to hunt.",
                      })}
                    />
                    <label htmlFor="bb-target_markets">Target markets*</label>
                    <p className="mt-1.5 text-xs" style={{ color: "#A8A29A" }}>
                      Cities, counties, metros, or zip codes — list as many as you like.
                    </p>
                    {errors.target_markets && (
                      <p className="error-text" data-testid="bb-error-target-markets">
                        {errors.target_markets.message}
                      </p>
                    )}
                  </div>

                  <fieldset data-testid="bb-lot-types-group">
                    <legend
                      className="mb-3"
                      style={{
                        color: "#122B20",
                        fontSize: "0.72rem",
                        letterSpacing: "0.22em",
                        textTransform: "uppercase",
                        fontWeight: 500,
                      }}
                    >
                      Lot types (pick any)
                    </legend>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      {LOT_TYPES.map((lt) => (
                        <label
                          key={lt}
                          className="flex items-center gap-2.5 rounded-xl px-4 py-3 cursor-pointer transition-colors"
                          style={{
                            background: "rgba(250,247,242,0.55)",
                            border: "1px solid rgba(168,162,154,0.45)",
                          }}
                          data-testid={`bb-lot-${lt.replace(/\s+/g, "-").toLowerCase()}-label`}
                        >
                          <input
                            type="checkbox"
                            value={lt}
                            className="w-4 h-4 accent-[#1B4332]"
                            data-testid={`bb-lot-${lt.replace(/\s+/g, "-").toLowerCase()}`}
                            {...register("lot_types")}
                          />
                          <span
                            className="font-body text-[#2B2926]"
                            style={{ fontSize: "0.92rem" }}
                          >
                            {lt}
                          </span>
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <SelectField
                      id="bb-lot_budget"
                      label="Typical lot budget"
                      testId="bb-input-lot-budget"
                      register={register("lot_budget")}
                      options={[
                        "Under $50k",
                        "$50k–$150k",
                        "$150k–$400k",
                        "$400k–$1M",
                        "$1M+",
                      ]}
                    />
                    <SelectField
                      id="bb-utility_requirements"
                      label="Utility requirements"
                      testId="bb-input-utility"
                      register={register("utility_requirements")}
                      options={[
                        "Public water & sewer required",
                        "Well & septic acceptable",
                        "Depends on deal",
                      ]}
                    />
                  </div>

                  <div className="field">
                    <input
                      id="bb-product_price"
                      type="text"
                      placeholder=" "
                      data-testid="bb-input-product"
                      {...register("product_price")}
                    />
                    <label htmlFor="bb-product_price">Product &amp; price point</label>
                    <p className="mt-1.5 text-xs" style={{ color: "#A8A29A" }}>
                      What do you build, and at what finished price range?
                    </p>
                  </div>

                  <SelectField
                    id="bb-close_timeline"
                    label="Close timeline"
                    testId="bb-input-close-timeline"
                    register={register("close_timeline")}
                    options={["14 days", "30 days", "45+ days", "Depends on deal"]}
                  />

                  <SelectField
                    id="bb-lot_volume"
                    label="Expected lot volume*"
                    testId="bb-input-lot-volume"
                    register={register("lot_volume", {
                      required: "Give us a sense of your buying volume.",
                    })}
                    options={[
                      "1–2 lots per quarter",
                      "3–5 lots per quarter",
                      "6+ lots per quarter",
                      "A few per year — scaling up",
                    ]}
                    error={errors.lot_volume}
                    errorTestId="bb-error-lot-volume"
                  />

                  <div className="field">
                    <textarea
                      id="bb-notes"
                      rows={4}
                      placeholder=" "
                      data-testid="bb-input-notes"
                      {...register("notes")}
                    />
                    <label htmlFor="bb-notes">Deal-killers &amp; notes</label>
                    <p className="mt-1.5 text-xs" style={{ color: "#A8A29A" }}>
                      Flood zones, dirt roads, HOA lots, minimum spread — tell us what makes you pass.
                    </p>
                  </div>

                  <label
                    className="flex items-start gap-3 pt-1 cursor-pointer select-none"
                    data-testid="bb-consent-label"
                  >
                    <input
                      type="checkbox"
                      className="mt-1 w-4 h-4 accent-[#1B4332]"
                      data-testid="bb-input-consent"
                      {...register("consent", {
                        required:
                          "Please confirm you agree to be contacted about land opportunities.",
                      })}
                    />
                    <span
                      className="font-body text-[#2B2926]/85"
                      style={{ fontSize: "0.88rem", lineHeight: 1.6 }}
                    >
                      I agree to be contacted by Ibex Acquisitions &amp;
                      Development about land opportunities.
                    </span>
                  </label>
                  {errors.consent && (
                    <p className="error-text" data-testid="bb-error-consent">
                      {errors.consent.message}
                    </p>
                  )}

                  {status.state === "error" && (
                    <div
                      className="flex items-start gap-3 p-4 rounded-xl"
                      style={{
                        background: "rgba(138, 43, 43, 0.08)",
                        border: "1px solid rgba(138, 43, 43, 0.3)",
                      }}
                      data-testid="buy-box-error"
                    >
                      <AlertCircle size={18} color="#8a2b2b" strokeWidth={2} className="mt-0.5 shrink-0" />
                      <p
                        className="font-body text-[#8a2b2b]"
                        style={{ fontSize: "0.9rem", lineHeight: 1.6 }}
                      >
                        {status.error} Please try again in a moment.
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting || status.state === "submitting"}
                    className="btn-primary w-full mt-2 disabled:opacity-70 disabled:cursor-wait"
                    data-testid="buy-box-submit"
                  >
                    {isSubmitting || status.state === "submitting" ? (
                      <span>Sending…</span>
                    ) : (
                      <>
                        <span>Send My Buy Box</span>
                        <ArrowRight size={18} strokeWidth={1.75} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* Small custom select styled as a "field" */
const SelectField = ({ id, label, options, register, testId, error, errorTestId }) => (
  <div className="field">
    <select
      id={id}
      data-testid={testId}
      className="appearance-none"
      style={{
        width: "100%",
        background: "rgba(250,247,242,0.55)",
        border: "1px solid rgba(168,162,154,0.45)",
        borderRadius: "0.85rem",
        padding: "1.25rem 1rem 0.55rem",
        fontFamily: "Inter, sans-serif",
        fontSize: "0.95rem",
        color: "#2B2926",
        outline: "none",
      }}
      defaultValue=""
      {...register}
    >
      <option value="" disabled hidden>
        &nbsp;
      </option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
    <label
      htmlFor={id}
      style={{
        position: "absolute",
        top: "0.5rem",
        left: "1.05rem",
        color: "#1B4332",
        fontSize: "0.68rem",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        fontWeight: 500,
        pointerEvents: "none",
      }}
    >
      {label}
    </label>
    <div
      className="absolute pointer-events-none"
      style={{ right: "1rem", top: "1.15rem", color: "#A8A29A" }}
    >
      <ChevronDown size={16} strokeWidth={1.75} />
    </div>
    {error && (
      <p className="error-text" data-testid={errorTestId}>
        {error.message}
      </p>
    )}
  </div>
);

/* ---------------- PAGE ---------------- */
const BuildersPage = () => (
  <main data-testid="builders-page">
    <BuildersHero />
    <Problem />
    <HowWeWork />
    <WhatWeDeliver />
    <PartnershipStandard />
    <Gallery />
    <BeforeAfter />
    <BuilderEdition />
    <WhoWeAre />
    <BuyBoxForm />
  </main>
);

export default BuildersPage;
